/**
 * Korean message catalog.
 *
 * Typed as Record<MessageKey, string>, so it must define every key in en.ts —
 * a missing or extra key is a compile error. Values are the translations.
 * Terminology aligned with the sibling p2p platform's Korean UI (승인 대기,
 * Bitcoin 네트워크, 로그아웃, 테마) for cross-app consistency.
 */
import type { MessageKey } from './en';

export const ko: Record<MessageKey, string> = {
  // Common
  'common.unknown': '알 수 없음',
  'common.tryAgain': '다시 시도',
  'common.walletFallback': '지갑',
  'common.loadingWallets': '지갑 로딩 중...',
  'common.back': '뒤로',
  'common.close': '닫기',
  'common.loading': '로딩 중...',
  'common.select': '선택',
  'common.selected': '선택됨',
  'common.beta': 'BETA',
  'common.cancel': '취소',
  'common.refresh': '새로고침',
  'common.refreshing': '새로고침 중',
  'common.all': '전체',
  'common.wallets': '지갑',
  'common.wallet': '지갑',
  'common.networks': '네트워크',
  'common.network': '네트워크',
  'common.pending': '대기 중',
  'common.unavailable': '사용 불가',
  'common.unverified': '미검증',
  'common.settings': '설정',
  'menu.title': '메뉴',
  'menu.open': '메뉴 열기',
  'menu.close': '메뉴 닫기',
  'common.tier.testnet': '테스트넷',
  'common.tier.mainnet': '메인넷',
  'common.theme.auto': '자동',
  'common.theme.light': '라이트',
  'common.theme.dark': '다크',

  // Navigation
  'nav.primary': '주요',
  'nav.home': '홈',

  // Settings — page
  'settings.title': '설정',

  // Settings — approvals section
  'settings.approvals.title': '승인',
  'settings.approvals.hint': '게이트웨이가 서명하기 전에 승인 여부를 결정해야 하는 거래를 검토하세요.',
  'settings.approvals.link': '승인 대기 항목 검토',

  // Settings — appearance section
  'settings.appearance.title': '화면 설정',
  'settings.appearance.hint': '앱 표시 방식을 선택하세요.',
  'settings.appearance.themeGroup': '테마',

  // Settings — language section
  'preauth.menu.title': '언어 및 화면 설정',
  'preauth.menu.open': '언어 및 화면 설정 메뉴 열기',
  'settings.language.title': '언어',
  'settings.language.hint': '원하는 언어를 선택하세요.',

  // Settings — developer mode section
  'settings.devmode.title': '개발자 모드',
  'settings.devmode.label': '개발자 모드',
  'settings.devmode.desc': '켜면 테스트넷, 끄면 메인넷을 사용합니다.',
  'settings.devmode.note': '테스트넷 모드입니다. 잔액은 실제 가치가 없습니다.',

  // Settings — deployment section
  'settings.deployment.title': '배포 정보',
  'settings.deployment.network': '네트워크',
  'settings.deployment.gatewayEnv': '게이트웨이 환경',
  'settings.deployment.bitcoinNetwork': 'Bitcoin 네트워크',
  'settings.deployment.organization': '조직',

  // Settings — security section (device passkeys)
  'settings.security.title': '보안',
  'settings.security.hint':
    '이 기기에서 거래를 승인하는 기기 패스키를 추가하면 게이트웨이가 서명하기 전에 확인합니다.',
  'settings.security.methodsTitle': '인증 수단',
  'settings.security.addDevice': '인증수단 추가',
  'settings.security.limitReached': '최대 {max}개의 인증 수단만 등록할 수 있습니다.',
  'settings.security.adding': '기기를 기다리는 중…',
  'settings.security.remove': '삭제',
  'settings.security.removing': '삭제 중…',
  'settings.security.empty': '등록된 기기 패스키가 없습니다.',
  'settings.security.loading': '인증 방법을 불러오는 중…',
  'settings.security.retry': '다시 시도',
  'settings.security.unsupported': '이 브라우저는 기기 패스키를 지원하지 않습니다.',
  'settings.security.enrolledOn': '{date} 추가됨',
  'settings.security.lastUsed': '마지막 사용 {date}',
  'settings.security.deviceFallbackLabel': '기기 패스키',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': '패스키 만들기',
  'passkey.create.body': '지문, 얼굴 또는 화면 잠금으로 로그인하세요. 기억할 비밀번호가 없습니다.',
  'passkey.create.cta': '패스키 만들기',
  'passkey.create.creating': '기기를 기다리는 중…',
  'passkey.create.dismiss': '다시 표시하지 않기',
  'passkey.create.error': '패스키를 만들 수 없습니다. 다시 시도하거나 나중에 메뉴의 보안에서 추가하세요.',
  'passkey.stateUnknown':
    '출금은 진행되지 않았습니다. 연결 문제로 인증 수단을 확인하지 못했으니 다시 시도하세요.',

  // Settings — account section
  'settings.account.title': '계정',
  'settings.account.signout': '로그아웃',

  // Login — page
  'login.title': '지갑으로 계속하기',
  'login.eyebrow': '환영합니다',
  'login.error.start': '로그인을 시작할 수 없습니다. 연결 상태를 확인한 후 다시 시도하세요.',
  'login.divider': '또는',
  'login.more': '다른 로그인 방법',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': 'Google 로그인',
  'login.provider.apple': 'Apple 로그인',
  'login.provider.discord': 'Discord 로그인',
  'login.provider.twitter': 'X 로그인',
  'login.provider.kakao': '카카오 로그인',
  'login.provider.fallback': '{name} 로그인',
  'login.provider.unavailable': '현재 이 로그인 방법은 사용할 수 없습니다.',

  // Login — passkey sign-in
  'login.passkey.button': '안전한 로그인',
  'login.passkey.desc': '얼굴·지문 인증 등 기기에 등록된 인증 방법을 사용합니다.',
  'login.passkey.pending': '패스키를 기다리는 중…',
  'login.passkey.cancelled':
    '패스키 로그인이 취소되었거나 이 기기에 이 사이트용 패스키가 없습니다. 다시 시도하거나 다른 로그인 방법을 선택하세요.',
  'login.passkey.error': '패스키로 로그인할 수 없습니다. 다시 시도하거나 다른 로그인 방법을 선택하세요.',
  'login.passkey.noAccount': '이 기기에서 패스키를 찾을 수 없습니다. 아래 "지갑 만들기"로 만들 수 있습니다.',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': '처음이신가요?',
  'login.signup.link': '지갑 만들기',
  'login.signup.pending': '계정을 만드는 중…',
  'login.signup.exists': '이미 계정이 있습니다. 패스키로 로그인하세요.',
  'login.signup.error': '계정을 만들 수 없습니다. 다시 시도하거나 다른 로그인 방법을 선택하세요.',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': '조직 ID',
  'login.org.note': '자신의 공간으로 로그인하려면 비워 두세요. 그런 다음 위에서 로그인 방법을 선택하세요.',

  // Login — developer fallback
  'login.dev.body': '게이트웨이를 대상으로 로컬 개발 시 브라우저 콘솔에서 토큰을 설정하세요:',

  // Dashboard (home)
  'dashboard.offline': '오프라인입니다. 연결 후 지갑을 다시 불러오세요.',
  'dashboard.empty': '아직 지갑이 없습니다. 지갑을 생성하여 시작하세요.',

  // Home components
  'home.account.allWallets': '전체 지갑',
  'home.account.subtitle': '{wallets}개 {walletWord} · {networks}개 {networkWord}',
  'home.switcher.aria': '계정 전환, 현재 {name}',
  'home.copy.aria': '{name} 주소 복사',
  'home.portfolio.aria': '포트폴리오 잔액',
  'home.portfolio.total': '총 보유 자산',
  'home.actions.aria': '지갑 작업',
  'home.action.swapUnavailable': '교환은 아직 사용할 수 없습니다.',
  'home.action.buySellUnavailable': '매수와 매도는 아직 사용할 수 없습니다.',
  'home.action.buySell': '매수/매도',
  'home.network.testnetTitle': '테스트넷 배포입니다. 여기에 표시된 잔액은 실제 가치가 없습니다.',
  'home.network.mainnetTitle': '메인넷입니다. 잔액은 실제 네트워크를 반영합니다.',

  // App navigation / account-wide surfaces
  'activity.title': '활동',
  'activity.history': '거래 내역',

  // Transaction list
  'tx.aria.history': '거래 내역',
  'tx.date.pending': '대기 중',
  'tx.direction.receive': '입금',
  'tx.direction.send': '출금',
  'tx.direction.swap': '교환',
  'tx.relation.from': '보낸 주소',
  'tx.relation.to': '받는 주소',
  'tx.swapDetails.aria': '교환 세부정보: {from}에서 {to}로',
  'tx.status.pending': '대기 중',
  'tx.status.pending_approval': '승인 대기 중',
  'tx.status.pending_policy': '정책 확인 중',
  'tx.status.signing': '서명 중',
  'tx.status.signed': '서명됨',
  'tx.status.submitted': '제출됨',
  'tx.status.broadcast': '브로드캐스트됨',
  'tx.status.confirmed': '확인됨',
  'tx.status.failed': '실패',
  'tx.status.rejected': '거절됨',
  'tx.status.unknown': '알 수 없음',
  'tx.loading': '활동을 불러오는 중...',
  'tx.empty': '아직 활동이 없습니다.',
  'tx.empty.hint': '출금과 교환 내역이 여기에 표시됩니다.',
  'tx.loadingMore': '더 불러오는 중...',
  'tx.explorer.aria': '블록 익스플로러에서 보기',

  // Approvals
  'approvals.title': '승인',
  'approvals.error.load': '승인을 불러올 수 없습니다',
  'approvals.empty.title': '모두 처리되었습니다',
  'approvals.empty.body':
    '결정 대기 중인 거래가 없습니다. 검토가 필요한 새 요청이 여기에 표시됩니다.',
  'approvals.wallet.title': '이 지갑의 승인',
  'approvals.wallet.resolving': '이 지갑에 속한 승인을 확인하는 중...',
  'approvals.wallet.empty.title': '이 지갑에 대기 중인 항목이 없습니다',
  'approvals.wallet.empty.body':
    '이 지갑에서 결정 대기 중인 거래가 없습니다. 검토가 필요한 요청이 여기에 표시됩니다.',
  'approvals.status.pending': '대기 중',
  'approvals.status.approved': '승인됨',
  'approvals.status.rejected': '거절됨',
  'approvals.status.expired': '만료됨',
  'approvals.quorum': '승인 {required}개 중 {received}개',
  'approvals.quorum.aria': '정족수 진행률',
  'approvals.waiting.more': '{word} {count}개 더 필요',
  'approvals.waiting.approval': '승인',
  'approvals.waiting.approvals': '승인',
  'approvals.waiting.signing': '서명 절차 완료 대기 중',
  'approvals.timeline.checker': '검토자',
  'approvals.timeline.acted': '처리함',
  'approvals.detail.loading': '정확한 거래를 불러오는 중...',
  'approvals.detail.unavailable':
    '거래 세부정보를 불러올 수 없습니다. 다른 방법으로 이 전송을 확인할 수 있을 때만 승인하거나 거절하세요.',
  'approvals.detail.notProvided': '거래 기록에서 제공되지 않음',
  'approvals.detail.requestedBy': '요청자',
  'approvals.detail.transactionStatus': '거래 상태',
  'approvals.summary.transaction': '거래',
  'approvals.action.busy':
    '다자간 서명을 조율하는 중입니다. 정족수가 충족되면 게이트웨이가 서명하고 브로드캐스트한 뒤 응답하며, 몇 초 정도 걸릴 수 있습니다.',
  'approvals.action.approve': '승인',
  'approvals.action.reject': '거절',
  'approvals.confirm.approveTitle': '이 거래를 승인하시겠습니까?',
  'approvals.confirm.rejectTitle': '이 거래를 거절하시겠습니까?',
  'approvals.confirm.approveConsequence':
    '승인 의사가 게이트웨이에 전달됩니다. 정족수가 충족되면 게이트웨이가 이 거래에 서명하고 네트워크에 전송한 뒤 응답하며, 몇 초 정도 걸릴 수 있습니다. 완료될 때까지 앱을 닫지 마세요.',
  'approvals.confirm.rejectConsequence':
    '거래 서명이 중단됩니다. 더 이상 정족수를 충족할 수 없어 브로드캐스트되지 않습니다.',
  'approvals.confirm.reasonLabel': '사유(선택 사항)',
  'approvals.confirm.reasonPlaceholder': '감사 기록용 메모 추가',
  'approvals.confirm.approving': '승인 중',
  'approvals.confirm.rejecting': '거절 중',
  'approvals.section.pending': '검토 필요 ({count})',
  'approvals.section.decided': '결정 완료',

  // Create wallet
  'create.title': '지갑 생성',
  'create.lead':
    '지갑 유형과 이름을 선택하세요. 키 생성은 서버에서 안전하게 실행되며, 이 기기는 키를 보관하거나 여기서 서명하지 않습니다.',
  'create.walletName': '지갑 이름',
  'create.walletPlaceholder': '내 지갑',
  'create.walletType': '지갑 유형',
  'create.kind.evm.label': 'EVM 계정',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · HyperEVM',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX 및 TRC-20 토큰',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL 및 SPL 토큰',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI 및 Sui 토큰',
  'create.kind.bitcoin.label': 'Bitcoin',
  'create.kind.bitcoin.desc': 'BTC · SegWit 주소',

  'provisioning.title': '지갑 생성 중',
  'provisioning.unavailable': '여기서는 지원되지 않음',
  'walletSettings.statusFrozen': '동결됨',
  'dashboard.stillCreating': '지갑 {count}개를 더 생성 중입니다.',
  'dashboard.createWallet': '지갑 만들기',
  'provisioning.failed': '지갑을 자동으로 생성하지 못했습니다.',
  'provisioning.createManually': '직접 지갑 만들기',
  'provisioning.succeeded': '지갑이 모두 준비되었습니다.',
  'provisioning.partial':
    '{created} 네트워크의 지갑은 성공적으로 생성되었지만, {missing} 네트워크의 지갑은 생성되지 않았습니다.',
  'provisioning.partialHint': '생성되지 않은 지갑은 나중에 직접 추가할 수 있습니다.',
  'provisioning.pending': '지갑이 아직 생성되고 있습니다.',
  'provisioning.pendingHint': '잠시 후 지갑이 홈 화면에 자동으로 표시됩니다.',
  'provisioning.goHome': '계속하기',
  'create.busy': '키 생성 절차 실행 중',
  'create.toast.created': '지갑이 생성되었습니다.',

  // OAuth callback
  'oauth.pending.title': '로그인 중',
  'oauth.pending.message': '로그인을 완료하는 중입니다. 잠시만 기다려 주세요.',
  'oauth.failure.title': '로그인이 완료되지 않았습니다',
  'oauth.failure.fallback': '로그인을 완료할 수 없습니다.',
  'oauth.backToSignIn': '로그인으로 돌아가기',

  // Receive ({chain} is the resolved chain name). KO uses p2p's "입금" (deposit) + "네트워크".
  'receive.title': '입금',
  'receive.subtitle': '입금할 네트워크와 주소를 선택하세요',
  'receive.chain.fallback': '이 지갑',
  'receive.chainSelector.aria': '입금할 네트워크를 선택하세요',
  'receive.addressPicker.label': '주소',
  'receive.addressPending': '주소 준비 중',
  'receive.qr.title': '{chain} 주소 QR 코드',
  'receive.qr.unavailable': 'QR 코드를 사용할 수 없습니다. 아래 주소를 복사하세요.',
  'receive.addressLabel': '{chain} 주소',
  'receive.warning': '이 주소로 {chain} 네트워크 자산만 보내세요',
  'receive.copy.copy': '주소 복사',
  'receive.copy.copied': '복사됨!',
  'receive.copy.failed': '복사 실패',
  'receive.copy.aria': '주소를 클립보드에 복사',
  'receive.pending.setup': '이 지갑은 아직 설정 중입니다. 준비되면 주소가 표시됩니다.',
  'receive.pending.loading': '지갑 주소를 불러오는 중...',
  'receive.empty': '아직 입금 가능한 지갑이 없습니다.',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render). KO uses "출금".
  'send.title': '출금',
  'send.selectAsset': '출금할 자산을 선택하세요',
  'send.loadingAssets': '자산을 불러오는 중...',
  'send.emptyAssets': '출금 가능한 잔액이 있는 자산이 아직 없습니다. 입금 후 출금하세요.',
  'send.emptyNoWallets': '아직 지갑이 없습니다. 지갑을 생성하여 출금하세요.',
  'send.backToAssets': '자산으로 돌아가기',
  'send.stepTwo.title': '{chain} 네트워크에서 {asset} 출금',
  'send.context.none': '선택된 지갑이 없습니다.',
  'send.context.from': '출금 지갑: {label} ({chain})',
  'send.context.fromUnsupported': '출금 지갑: {label}. {reason}',
  'send.error.wrongTier':
    '이 지갑은 현재 선택된 네트워크에 없습니다. 네트워크를 전환하거나 현재 네트워크의 지갑을 선택한 후 출금하세요.',
  'send.error.noWallet': '출금할 지갑을 선택하세요.',
  'send.error.chainUnavailable': '이 네트워크에서는 출금할 수 없습니다.',
  'send.status.submitting': '거래를 제출하는 중입니다.',
  'send.status.buildingPayload': '네트워크 수수료와 논스를 읽는 중입니다.',
  'send.status.pendingPolicy':
    '이 출금은 추가 승인이 필요하거나 정책상 대기 시간이 적용됩니다. 조건이 충족될 때까지 진행되지 않습니다.',
  'send.status.noApproval':
    '거래가 생성되었지만 여기서는 승인할 수 없습니다. 승인 화면에서 검토해 계속하세요.',
  'send.confirm.title': '출금 확인',
  'send.confirm.note': '받는 주소를 주의 깊게 확인하세요. 자금이 이동되며 되돌릴 수 없습니다.',
  'send.confirm.to': '받는 주소',
  'send.confirm.amount': '금액',
  'send.confirm.network': '네트워크',
  'send.confirm.networkFee': '네트워크 수수료',
  'send.confirm.networkFeeValue': '게이트웨이가 거래 생성 시 예상 수수료를 산정해 적용합니다.',
  'send.confirm.networkFeeValueEvm': '확인하는 시점에 네트워크에서 조회하여 거래에 적용됩니다.',
  'send.confirm.submit': '확인하고 출금',
  'send.confirm.sending': '출금 처리 중...',
  'send.status.title': '출금 상태',
  'send.status.statusLabel': '상태',
  'send.status.txId': '거래 ID',
  'send.status.hash': '거래 해시',
  'send.status.explorer': '블록 탐색기에서 보기',
  'send.status.goToApprovals': '승인 화면으로 이동',
  'send.status.backToSend': '출금으로 돌아가기',
  'send.result.status': '출금 {status}',
  'send.result.statusReason': '출금 {status}{reason}',

  // Send form and asset picker
  'send.form.recipient': '받는 주소',
  'send.form.destinationPlaceholder': '받는 주소',
  'send.form.noWallet': '출금할 지갑을 선택하세요.',
  'send.form.chainUnavailable': '이 네트워크에서는 여기서 출금할 수 없습니다.',
  'send.validation.recipientRequired': '받는 주소를 입력하세요.',
  'send.validation.evmWebUnavailable': '이 네트워크에서는 웹 앱에서 출금할 수 없습니다.',
  'send.validation.evmNoNetwork': '보낼 코인을 선택하면 네트워크가 결정됩니다. 이 계정은 모든 EVM 네트워크에서 하나의 주소를 사용합니다.',
  'send.validation.evmTokenUnsupported': 'EVM 네트워크의 토큰 전송은 아직 웹 앱에서 지원되지 않습니다. 토큰은 모바일 앱을 이용하거나, 여기서는 해당 네트워크의 기본 코인을 보내세요.',
  'send.validation.evmImportedTokenUnsupported': '가져온 토큰은 웹 앱에서 보낼 수 없습니다. 레지스트리에서 검증된 토큰과 네트워크 자체 코인만 여기에서 보낼 수 있습니다.',
  'send.validation.evmTokenDecimalsUnknown': '이 토큰의 정밀도를 확인할 수 없어 전송을 안전하게 인코딩할 수 없습니다. 아무것도 서명되지 않았습니다.',
  'send.validation.evmPrefix': 'EVM 주소는 0x로 시작합니다.',
  'send.validation.evmFormat': 'EVM 주소는 0x 뒤에 16진수 40자가 옵니다.',
  'send.validation.evmChecksum': '이 EVM 주소는 체크섬이 올바르지 않습니다.',
  'send.validation.evmZeroAddress': '제로 주소입니다. 이 주소로 보낸 자산은 복구할 수 없습니다.',
  'send.validation.suiPrefix': 'Sui 주소는 0x로 시작합니다.',
  'send.validation.suiFormat': 'Sui 주소는 0x 뒤에 소문자 16진수 64자가 이어집니다.',
  'send.validation.tronPrefix': 'Tron 주소는 T로 시작합니다.',
  'send.validation.tronLength': 'Tron 주소는 34자입니다.',
  'send.validation.tronChecksum': '이 Tron 주소의 체크섬이 올바르지 않습니다.',
  'send.validation.tronInvalid': '유효한 Tron 주소가 아닙니다.',
  'send.validation.solanaInvalid': 'Solana 주소 형식으로 보이지 않습니다.',
  'send.validation.solanaBytes': 'Solana 주소는 디코딩 시 32바이트여야 합니다.',
  'send.validation.bitcoinChecksum': '이 Bitcoin 주소의 체크섬이 올바르지 않습니다.',
  'send.validation.bitcoinInvalid': '유효한 Bitcoin 주소가 아닙니다.',
  'send.validation.bitcoinSegwitInvalid': '유효한 SegWit 주소가 아닙니다.',
  'send.validation.bitcoinTaprootInvalid': '유효한 Taproot 주소가 아닙니다.',
  'send.validation.bitcoinWrongNetwork':
    '다른 Bitcoin 네트워크의 주소입니다(예상: {expected}).',
  'send.validation.bitcoinDust': '금액이 Bitcoin 더스트 한도 {sats} sats보다 낮습니다.',
  'send.form.amountRequired': '금액을 입력하세요.',
  'send.form.numberRequired': '숫자를 입력하세요.',
  'send.form.amountPositive': '금액은 0보다 커야 합니다.',
  'send.form.decimals': '{symbol}는 소수점 이하 최대 {decimals}자리까지 지원합니다.',
  'send.form.exceedsBalance': '금액이 {symbol} 잔액을 초과합니다.',
  'send.form.assetAria': '자산',
  'send.form.noAssets': '자산 없음',
  'send.form.available': '사용 가능: {amount} {symbol}',
  'send.form.review': '출금 검토',
  'send.form.clear': '지우기',
  'send.assets.searchLabel': '자산 검색',
  'send.assets.searchPlaceholder': '이름 또는 심볼 검색',
  'send.assets.empty': '일치하는 자산이 없습니다. 검색어나 필터를 지우면 보유 자산을 볼 수 있습니다.',

  // Swap ({decimals}, {provider}, {count} are interpolated at render). KO uses p2p's "교환".
  'swap.title': '교환',
  'swap.subtitle': '모든 제공업체 중 최적 경로',
  'swap.balance': '잔액 {amount} {symbol}',
  'swap.max': '최대',
  'swap.signer.overridden': '{selected}은(는) 스왑할 수 없어 {label} · {address}을(를) 사용합니다.',
  'swap.empty.evm': '교환하려면 EVM 주소가 있는 지갑이 필요합니다. 먼저 홈 화면에서 생성하세요.',
  'swap.error.loadChains': '네트워크를 불러오지 못했습니다',
  'swap.error.quote': '교환 견적을 불러오지 못했습니다',
  'swap.error.executeNotEnabled':
    '이 서비스에서는 교환 실행이 활성화되어 있지 않습니다(개발 서명자 꺼짐).',
  'swap.error.executeFallback': '교환 실패',
  'swap.side.from': '보내는 금액',
  'swap.side.to': '받는 금액',
  'swap.chain.fromAria': '보낼 네트워크',
  'swap.chain.toAria': '받을 네트워크',
  'swap.amount.fromAria': '보낼 금액',
  'swap.amount.toAria': '받을 금액',
  'swap.amount.invalidHint': '소수점 이하 최대 {decimals}자리의 양수를 입력하세요.',
  'swap.flip.aria': '교환 방향 전환',
  'swap.sameTokenHint': '서로 다른 두 토큰을 선택하세요.',
  'swap.warning.insufficientBalance': '잔액이 이 금액보다 적어 교환이 실패합니다.',
  'swap.warning.insufficientAllowance': '교환 전에 토큰 승인이 필요합니다.',
  'swap.toast.submitted': '{provider}를 통해 교환 요청을 제출했습니다. 활동에서 확인하세요.',
  'swap.confirm.swapping': '교환 중...',
  'swap.confirm.via': '{provider}로 교환',
  'swap.confirm.summary': '{provider}를 통해 {inAmount} {inSymbol}을(를) 약 {outAmount} {outSymbol}(으)로 교환합니다. 정산은 대상 네트워크에서 비동기적으로 완료됩니다.',
  'swap.mpcNote': 'MPC 서명 플로우를 통해 지갑이 서명합니다. 원본 트랜잭션은 온체인으로 브로드캐스트되고 제공자가 체결을 정산합니다.',
  'swap.confirm.default': '교환',
  'swap.confirm.disabledTitle': '교환 실행은 게이트웨이 서명 연동과 함께 제공됩니다',
  'swap.mock.enabledHint': '개발 지갑(모의 MPC)으로 서명됨 · 실제 온체인 교환',
  'swap.mock.disabledHint': '실시간 견적 제공 · 교환 실행은 게이트웨이 서명 흐름과 함께 제공됩니다',
  'swap.picker.tokenAria': '토큰 선택',
  'swap.picker.chainAria': '네트워크 선택',
  'swap.picker.searchToken': '이름, 심볼 또는 주소 검색',
  'swap.picker.searchChain': '지갑 검색',
  'swap.picker.loadingTokens': '토큰 로딩 중...',
  'swap.picker.noTokens': '일치하는 토큰이 없습니다.',
  'swap.picker.noChains': '일치하는 네트워크가 없습니다.',
  'swap.picker.noWallets': '라우팅 가능한 체인에서 받을 수 있는 지갑이 없습니다.',
  'swap.picker.routes': '{count}개 경로',
  'swap.token.aria': '토큰 선택',
  'swap.quote.noRoute': '경로 없음',
  'swap.quote.best': '최적',
  'swap.quote.networkCost': '네트워크 수수료',
  'swap.quote.platformFee': '플랫폼 수수료',
  'swap.quote.priceImpact': '가격 영향',
  'swap.quote.time': '시간',

  // Swap detail sheet
  'swap.detail.aria': '교환 세부정보',
  'swap.detail.closeAria': '세부정보 닫기',
  'swap.detail.status': '상태',
  'swap.detail.paid': '보낸 금액',
  'swap.detail.received': '받은 금액',
  'swap.detail.rate': '환율',
  'swap.detail.route': '경로',
  'swap.detail.provider': '제공업체',
  'swap.detail.feeQuoted': '수수료(견적)',
  'swap.detail.feeCharged': '수수료(청구)',
  'swap.detail.sourceTx': '출발 거래',
  'swap.detail.destinationTx': '도착 거래',
  'swap.origin.unreported': '제출 중입니다. 아직 결과를 알 수 없습니다.',
  'swap.origin.reported': '네트워크에서 해당 거래를 확인 중입니다.',
  'swap.origin.verified': '출발 체인에서 전송이 확인되었습니다. 도착 체인을 기다리는 중입니다.',
  'swap.origin.reverted': '거래가 실행되었지만 실패했습니다. 자금은 이동하지 않았습니다.',
  'swap.origin.unknown':
    '이 거래의 처리 여부를 확인할 수 없습니다. 다시 시도하기 전에 지갑 내역을 확인하세요.',
  'swap.origin.noRetry': '이 거래가 확인되거나 실행되지 않은 것으로 판명될 때까지 다시 시도할 수 없습니다.',
  'swap.origin.ackPrompt': '이 교환은 아직 확인되지 않았습니다. 해제하기 전에 지갑 내역을 확인하세요.',
  'swap.origin.ackRelease': '지갑 내역을 확인했습니다 — 이 보류 해제',
  'swap.origin.trackingFailed':
    '이 교환을 기록할 수 없어 시작하지 않았습니다. 아무것도 서명되지 않았으며 자금도 이동하지 않았습니다.',

  // Earn (대출 마켓)
  'earn.title': '대출 & 차입',
  'earn.subtitle': '보유 자산을 담보로 자금을 빌리거나, 예치하여 수익을 얻어보세요.',
  'earn.error.market': '마켓을 불러올 수 없습니다: {message}',
  'earn.error.retry': '다시 시도',
  'earn.loading': '대출 마켓을 불러오는 중…',
  'earn.empty': '현재 이용할 수 있는 대출 마켓이 없습니다.',
  'earn.table.asset': '자산',
  'earn.table.totalSupply': '총 예치',
  'earn.table.totalBorrow': '총 차입',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': '대출 마켓',
  'earn.vault.title': '{symbol} 볼트',
  'earn.vault.supplyYield': '예치 수익률',
  'earn.vault.borrowInterest': '차입 이자',
  'earn.vault.marketPrice': '시장 가격',
  'earn.vault.utilization': '이용률',
  'earn.vault.depositThreshold': '예치 한도',
  'earn.vault.borrowThreshold': '차입 한도',
  'earn.vault.borrowFee': '차입 수수료: {pct}%',
  'earn.account.title': '내 계정',
  'earn.account.deposits': '내 예치금',
  'earn.account.borrows': '내 차입금',
  'earn.account.borrowLimit': '차입 가능 한도',
  'earn.account.liqThreshold': '청산 기준',
  'earn.account.health': '계정 건전성',
  'earn.account.depositedRow': '{amount} {symbol} 예치됨 (${usd})',
  'earn.account.borrowedRow': '{amount} {symbol} 차입됨 (${usd})',
  'earn.action.supply': '예치',
  'earn.action.borrow': '차입',
  'earn.action.withdraw': '출금',
  'earn.action.repay': '상환',
  'earn.actions.aria': '대출 작업',
  'earn.amount.label': '수량 ({symbol})',
  'earn.amount.available': '가능 ~{amount}',
  'earn.amount.max': '최대',
  'earn.amount.allDebt': '전액 상환',
  'earn.submit.submitting': '처리 중…',
  'earn.note.sameAssetBorrow':
    '{symbol}은(는) 담보 자산이라 차입할 수 없습니다. 다른 자산을 선택하거나 예치금을 먼저 출금하세요.',
  'earn.note.overMax.withdraw':
    '약 {amount} {symbol}까지만 출금할 수 있습니다. 나머지는 차입금의 담보로 유지되어야 합니다. 전액 출금하려면 먼저 상환하세요.',
  'earn.note.overMax.borrow': '담보 기준 최대 약 {amount} {symbol}까지 차입할 수 있습니다.',
  'earn.note.overMax.repay': '상환할 금액은 약 {amount} {symbol}입니다.',
  'earn.note.noSigner':
    '실행할 수 없습니다: 서명은 게이트웨이를 통해 이루어지며, 게이트웨이 대출 경로와 로컬 개발 서명자 모두에 연결할 수 없습니다.',
  'earn.note.devSigner': '개발 서명자 (테스트넷): {address}',
  'earn.toast.confirmed': '{action} 완료 ({digest}…)',
  'earn.toast.failedReason': '{action} 실패: {reason}',
  'earn.toast.failedOnChain': '{action} 실패: 온체인 오류 ({digest}…)',
  'earn.activity.title': '최근 활동',

  // Filters and lists
  'filter.network.aria': '네트워크 필터',
  'filter.chain.aria': '네트워크 필터',
  'filter.chain.selectNetwork': '네트워크 선택',
  'holdings.empty': '아직 보유 자산이 없습니다. 입금하거나 필터를 전환해 더 확인하세요.',
  'holdings.showHidden': '숨겨진 {count}개 표시 · $0.01 미만',
  'holdings.hideSmall': '작은 {word} {count}개 숨기기',
  'holdings.balance': '잔액',
  'holdings.balances': '잔액',
  'chainSummary.empty': '아직 보유 자산이 없습니다. 입금하면 네트워크가 표시됩니다.',
  'chainSummary.viewAria': '{name} 보기, {count}개 {word}',
  'walletChain.empty': '이 네트워크에는 아직 지갑이 없습니다.',
  'walletChain.openAria': '{label} 열기',

  // Account and wallet management sheets
  'account.closeSwitcher': '지갑 전환 닫기',
  'account.title': '내 지갑',
  'account.allDesc': '모든 지갑을 한 목록에서',
  'account.newWallet': '새 지갑',
  'account.searchPlaceholder': '이름 또는 주소 검색',
  'account.searchAria': '이름 또는 주소로 지갑 검색',
  'account.networks': '네트워크',
  'account.empty.filtered': '필터와 일치하는 지갑이 없습니다.',
  'account.empty.none': '아직 지갑이 없습니다.',
  'account.selectAria': '{label} 선택, {value}',
  'account.copyAddressAria': '{chain} 주소 복사',
  'account.showQrAria': '{chain} 입금 QR 표시',
  'selectNetwork.closeAria': '네트워크 선택 닫기',
  'selectNetwork.title': '네트워크 선택',
  'selectNetwork.searchPlaceholder': '네트워크 검색',
  'selectNetwork.searchAria': '이름으로 네트워크 검색',
  'selectNetwork.groupAria': '네트워크',
  'selectNetwork.all': '전체 네트워크',
  'selectNetwork.empty': '검색과 일치하는 네트워크가 없습니다.',
  'walletTabs.sectionsAria': '지갑 섹션',
  'walletTabs.tokens': '토큰',
  'walletTabs.approvals': '승인',
  'walletTabs.manage': '암호화폐 관리',
  'walletTabs.history': '거래 내역',
  'manage.closeAria': '암호화폐 관리 닫기',
  'manage.aria': '{wallet} 암호화폐 관리',
  'manage.title': '암호화폐 관리',
  'manage.searchPlaceholder': '토큰 검색',
  'manage.searchAria': '심볼 또는 이름으로 토큰 검색',
  'manage.empty': '이 지갑에는 아직 토큰이 없습니다. 입금 후 여기에서 관리하세요.',
  'manage.noMatches': '검색과 일치하는 토큰이 없습니다.',
  'manage.tokensAria': '토큰',
  'manage.showTokenAria': '{network}의 {symbol} 표시',
  'manage.import': '맞춤 토큰 가져오기',
  'manage.note':
    '이 지갑이 보유한 토큰만 표시됩니다. 계약 주소로 토큰을 가져와 맞춤 자산을 추적하세요. 가져온 토큰은 검증되지 않았으며 가격이 없습니다.',
  'import.closeAria': '토큰 가져오기 닫기',
  'import.aria': '{wallet}에 토큰 가져오기',
  'import.title': '토큰 가져오기',
  'import.contract': '계약 주소',
  'import.contractPlaceholder': '토큰 계약 주소 붙여넣기',
  'import.contractAria': '토큰 계약 주소',
  'import.resolve': '토큰 조회',
  'import.resolving': '조회 중...',
  'import.unknownToken': '알 수 없는 토큰',
  'import.unnamedToken': '이름 없는 토큰',
  'import.decimals': '소수점 자리',
  'import.balance': '잔액',
  'import.hint':
    '{network}의 토큰 계약 주소를 붙여넣고 조회하여 추가 전에 토큰을 미리 확인하세요.',
  'import.warning':
    '미검증 토큰입니다. 추가하기 전에 계약 주소를 직접 확인하세요. 가져온 토큰은 선별 목록에 없으며 가격이 없습니다.',
  'import.add': '토큰 추가',
  'import.error.cap': '이 지갑은 가져온 토큰 최대 개수에 도달했습니다. 하나를 제거한 후 추가하세요.',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': '이 토큰은 선별 목록에 없습니다.',
  'walletBalance.testnetPillTitle': '테스트넷 지갑입니다. 잔액은 실제 가치가 없습니다.',
  'walletBalance.testnet': '테스트넷',
  'walletBalance.loadingBalances': '잔액 로딩 중...',
  'walletBalance.statusPrefix': '상태: {status}',
  'walletBalance.keygen': '키 생성이 진행 중입니다. 준비되면 잔액이 표시됩니다.',
  'walletBalance.partial': '일부 잔액을 읽을 수 없어 불완전할 수 있습니다.',
  'walletBalance.chainFilterAria': '네트워크 필터',
  'walletBalance.testnetBanner':
    '테스트넷입니다. 여기에 표시된 잔액은 테스트넷 잔액이며 실제 가치가 없습니다. 메인넷을 사용하려면 메뉴에서 개발자 모드를 끄세요.',
  'walletSettings.addressAria': '지갑 주소',
  'walletSettings.detailsAria': '지갑 세부정보',
  'walletSettings.address': '주소',
  'walletSettings.details': '세부정보',
  'walletSettings.addressPending': '주소 준비 중입니다. 지갑 키 생성이 완료되면 표시됩니다.',
  'walletSettings.name': '이름',
  'walletSettings.balance': '잔액',
  'walletSettings.status': '상태',
  'walletSettings.curve': '곡선',
  'walletSettings.publicKey': '공개 키',
  'walletSettings.shortAddress': '축약 주소',
  'walletSettings.pending': '대기 중',
  'walletSettings.unknownChain': '알 수 없는 네트워크',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': '환영합니다',
  'welcome.tagline': '소중한 것을 지키는 힘',
  'welcome.title': '언어를 선택하세요',
  'welcome.cta': '시작하기',
  'welcome.legalTitle': '법적 고지',
  'welcome.legalText': '언어 지원은 전 세계 사용자의 접근성을 향상시키기 위한 노력의 일환으로 제공됩니다. 이러한 사용이 법적으로 허용되는 지역에서만 사용할 수 있으며, 이를 선호하거나 필요로 하는 사용자의 편의를 위해서만 제공됩니다.',
  'welcome.legalAgreement': '아래 "동의합니다" 버튼을 클릭하면 이 언어 사용이 현지 법률 및 규정을 준수함을 확인합니다.',
  'welcome.agree': '동의합니다',

  'start.label': '지갑',
  'start.points.badge': 'MPC 기술 기반',
  'start.points.lead': '노출 위험이 큰 개인 키나 시드 문구가 필요하지 않습니다.',
  'start.points.shares':
    '지갑은 Google, 사용자의 백업, 기기 인증에 각각 분산된 보안 요소(key shard)로 안전하게 보호됩니다.',
  'start.points.twoRequired': '자산을 움직이려면 최소 두 개의 보안 요소가 함께 동의해야 합니다.',
  'start.points.neverWhole':
    '자산을 지키는 보안 요소들이 하나로 합쳐지거나 한곳에 저장되지 않기 때문에 안전합니다.',
  'start.points.rules': '보안 요소에 문제가 생겨도 추가 보안 규칙이 자산을 계속 지켜줍니다.',
  'start.points.more': '더 보기',
  'start.points.less': '접기',
  'start.title': '익명 지갑 만들기',
  'start.desc': '기기의 인증 기능을 사용해 탈중앙화 지갑을 보호하세요.',
  'start.hint': '얼굴 인식 · 지문 인식 · 패스키',
  'start.signin.prompt': '이미 계정이 있으신가요?',
  'start.signin.link': '로그인',
  'start.unsupported': '이 브라우저에서는 패스키를 만들 수 없습니다. 대신 외부 계정으로 로그인하세요.',

  // Maintenance screen. Shown when the host answers /v1 with 503.
  'maintenance.title': '지갑 점검 중',
  'maintenance.body': '현재 예정된 점검을 진행하고 있습니다. 점검이 완료되면 지갑을 다시 이용할 수 있습니다.',
  'maintenance.assurance': '자금과 키는 안전합니다. 로그아웃되어도 보유 자산에는 아무런 변화가 없습니다.',
  'maintenance.retry': '다시 확인',
  'start.secondAccount.title': '이 기기에는 이미 계정에 연결된 인증 수단이 등록되어 있습니다',
  'start.secondAccount.body': '다른 계정을 추가하시겠습니까?',
  'start.secondAccount.signIn': '로그인하기',
  'start.secondAccount.create': '다른 계정 추가',
};
